/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    BR-16_CONFIRM_RESULT_PAGE
 * Date:         2026-02-08
 * Modified By:  Claude
 *
 * Description:
 * Landing page shown after user clicks one-click confirm link in email.
 * Displays success or error message based on query parameters.
 *
 * File Size Check:
 * - Current: 0 lines
 * - Adding: ~100 lines
 * - Projected: 100 lines
 * - Decision: Proceed (within 150 line limit for components)
 *
 * Related Files:
 * - server/controllers/inventoriesController.js (confirmByToken)
 * - client/src/App.tsx (route)
 */

import React from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Container, Card, Button } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle, faTimesCircle, faBoxOpen } from "@fortawesome/free-solid-svg-icons";

const InventoryConfirmResult: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const status = searchParams.get("status");
  const reason = searchParams.get("reason");

  const isSuccess = status === "success";

  const getErrorMessage = () => {
    switch (reason) {
      case "expired":
        return "This confirmation link has expired. Please log in to confirm your inventory manually.";
      case "no-inventory":
        return "You don't have any active inventory to confirm. Please upload inventory first.";
      case "invalid":
      default:
        return "This confirmation link is invalid. Please log in to confirm your inventory manually.";
    }
  };

  return (
    <Container className="py-5">
      <div className="d-flex justify-content-center">
        <Card style={{ maxWidth: "500px", width: "100%" }}>
          <Card.Body className="text-center py-5">
            {isSuccess ? (
              <>
                <FontAwesomeIcon
                  icon={faCheckCircle}
                  size="4x"
                  className="text-success mb-4"
                />
                <h2 className="mb-3">Inventory Confirmed</h2>
                <p className="text-muted mb-4">
                  Your inventory has been successfully confirmed and will remain
                  visible to buyers.
                </p>
              </>
            ) : (
              <>
                <FontAwesomeIcon
                  icon={faTimesCircle}
                  size="4x"
                  className="text-danger mb-4"
                />
                <h2 className="mb-3">Confirmation Failed</h2>
                <p className="text-muted mb-4">{getErrorMessage()}</p>
              </>
            )}

            <Button
              variant="primary"
              size="lg"
              onClick={() => navigate("/inventory")}
              className="d-inline-flex align-items-center gap-2"
            >
              <FontAwesomeIcon icon={faBoxOpen} />
              Go to Inventory
            </Button>
          </Card.Body>
        </Card>
      </div>
    </Container>
  );
};

export default InventoryConfirmResult;
